import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { DesignTaskStatusBadge, DESIGN_TASK_STATUS_TONE } from "../components/etams/StatusBadge";
import { AlertCircle } from "lucide-react";
import { formatDate } from "../lib/format";
import { apiErrorMessage } from "../lib/inventoryApi";
import { labelPaket, labelProduk } from "../lib/faKonfigurasi";
import { useDaftar } from "../hooks/useDaftar";
import { PalangDaftar, Paginasi } from "../components/etams/PalangDaftar";

// Q68-01 Batch 5. Peta ini dulu ditulis literal DI SINI dan DIULANG kata demi
// kata di `DesignTaskDetail.jsx:13` — dua definisi untuk makna yang sama, pola
// yang persis melahirkan Q68-01. Bedanya: dua salinan itu masih SEPAKAT isinya,
// jadi belum ada gejala yang kelihatan. Yang diperbaiki di sini bukan gejala,
// tapi kemungkinannya — salinan di DesignTaskDetail sudah dihapus, dan yang
// tersisa DITURUNKAN dari `DESIGN_TASK_STATUS_TONE`.
//
// Pola turunan ini menyalin FALABEL-01 (`FA_STATUS_LABEL` ← `FA_ORDER_STATUS`
// di `lib/statusLabels.js:36`): satu sumber, label dan warna tidak bisa lagi
// berpisah diam-diam. Sumber di belakangnya adalah CHECK
// `design_tasks_status_check` (`004_phase1b_correction_flow.sql:329-330`).
//
// Tetap dibutuhkan meski badge sudah pindah komponen: label ini juga dipakai
// pilihan saringan dan chip saringan, yang bukan badge.
const TASK_STATUS_LABEL = Object.fromEntries(
  Object.entries(DESIGN_TASK_STATUS_TONE).map(([code, v]) => [code, v.l]),
);

// Tugas Desainer — pola daftar seragam mode SERVER (28 Sep 2026; `docs/POLA_DAFTAR.md`).
// Dulu plafon tampil 100 tanpa pindah halaman. Sekarang `total` dari server
// (`/design-tasks` sudah memberinya sejak 27 Sep), halaman + cari nomor PD di server.
// Urutan TETAP ditentukan server, bukan kolom: antrean aktif (Terbuka/Diambil) tertua
// dulu (FIFO), riwayat terbaru dulu (KELAS H) — karena itu kepala kolom tidak bisa diklik.
// Saringan bawaan: status Terbuka (antrean yang perlu diambil), terlihat sebagai chip;
// dihapus = semua status.
const STATUS_PILIHAN = ["OPEN", "CLAIMED", "COMPLETED"];

export default function DesignTasks() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [terpotong, setTerpotong] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const daftar = useDaftar(items, {
    server: { total, urutSah: [] },
    saringan: {
      status: { label: "Status", param: "status_eq", bawaan: "OPEN", tampil: (v) => TASK_STATUS_LABEL[v] || v },
    },
  });
  const { cari, limit, offset } = daftar.paramServer;
  const status = daftar.nilaiSaringan.status;

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      // ⚠ `status_eq` SELALU dikirim — juga saat kosong. Backend memakai "OPEN" bila
      //   parameter ini tidak ada, jadi saringan yang dihapus orang harus dikirim sebagai
      //   kosong ("semua status"), bukan dihilangkan.
      const r = await api.get("/design-tasks", {
        params: { status_eq: status || "", ...(cari && { q: cari }), limit, offset },
      });
      setItems(r.data?.items || []);
      setTotal(typeof r.data?.total === "number" ? r.data.total : (r.data?.items || []).length);
      setTerpotong(!!r.data?.terpotong);
    } catch (e) {
      setErr(apiErrorMessage(e, "Gagal memuat tugas desainer."));
    } finally {
      setLoading(false);
    }
  }, [status, cari, limit, offset]);

  useEffect(() => { load(); }, [load]);

  const antrean = status === "OPEN" || status === "CLAIMED";

  return (
    <div className="space-y-4" data-testid="design-tasks-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Tugas Desainer</h1>
      </div>

      <PalangDaftar daftar={daftar} placeholder="Cari nomor PD" testId="design-tasks-palang">
        <div className="flex gap-2">
          {STATUS_PILIHAN.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => daftar.setSaringan("status", s)}
              className={`px-3 py-1 text-xs rounded border ${status === s ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground border-border"}`}
              data-testid={`design-tasks-filter-${s}`}
            >
              {TASK_STATUS_LABEL[s]}
            </button>
          ))}
        </div>
      </PalangDaftar>

      {err && (
        <div className="flex items-start gap-2 p-3 rounded border border-danger-soft-border bg-danger-soft text-danger-soft-foreground text-sm" data-testid="design-tasks-error">
          <AlertCircle size={16} className="mt-0.5 shrink-0" /><span className="break-all">{err}</span>
        </div>
      )}

      {loading && daftar.tampil.length === 0 ? (
        <div className="text-sm text-muted-foreground">Memuat...</div>
      ) : daftar.tampil.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted-foreground" data-testid="design-tasks-empty">
          {daftar.cari.trim()
            ? "Tidak ada tugas yang cocok dengan pencarian ini."
            : status ? `Tidak ada tugas ${TASK_STATUS_LABEL[status] || status}.` : "Belum ada tugas."}
        </Card>
      ) : (
        <Card>
          <div className="px-4 pt-3 text-xs text-muted-foreground">
            {status ? (antrean ? "Urutan: tertua dulu (antrean)." : "Urutan: terbaru dulu.") : "Urutan: terbaru dulu."}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="design-tasks-table">
              <thead className="text-xs text-muted-foreground border-b border-border">
                <tr>
                  <th className="text-left px-4 py-2">Nomor PD</th>
                  <th className="text-left">Produk</th>
                  <th className="text-left">Paket</th>
                  <th className="text-left">Status</th>
                  <th className="text-left">Dibuat</th>
                </tr>
              </thead>
              <tbody>
                {daftar.tampil.map((t) => (
                  <tr key={t.id} onClick={() => navigate(`/design-tasks/${t.id}`)} className="border-b border-border cursor-pointer hover:bg-muted/50" data-testid={`design-task-row-${t.id}`}>
                    <td className="px-4 py-2 font-medium">{t.design_request?.dr_number || "—"}</td>
                    <td>{labelProduk(t.design_request?.product_summary_type)}</td>
                    <td>{labelPaket(t.design_request?.package_type)}</td>
                    <td><DesignTaskStatusBadge value={t.status} /></td>
                    <td className="text-xs text-muted-foreground">{formatDate(t.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {terpotong && (
            // KELAS H: jalur saringan cakupan di server berplafon pindai — kalau tersentuh,
            // layar WAJIB berkata begitu, bukan membiarkan orang mengira jumlahnya lengkap.
            <div className="px-4 py-2 text-xs text-warning-soft-foreground border-t border-border" data-testid="design-tasks-terpotong">
              Jumlah di atas hanya dari 5.000 tugas pertama yang sempat diperiksa — ada yang lebih banyak dari itu.
            </div>
          )}
          <Paginasi daftar={daftar} testId="design-tasks-paginasi" />
        </Card>
      )}
    </div>
  );
}
